/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'

import {
  EuiFormLabel,
  EuiSkeletonText,
  EuiSpacer,
  EuiFlyout,
  EuiFlyoutHeader,
  EuiTitle,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlexGroup,
  EuiFlexItem,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { TlsPublicCertChain, UpdatedTlsChain } from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, RegionId } from '@modules/ui-types'
import { CuiButton, CuiButtonEmpty } from '@modules/cui/Button'
import { CuiFileUploadButton } from '@modules/cui/FileUploadButton'
import { CuiCodeBlock } from '@modules/cui/CodeBlock'
import { CuiAlert } from '@modules/cui/Alert'
import CopyButton from '@modules/cui/CopyButton'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import { readFile } from '../../../../lib/fileHelpers'

interface Props {
  regionId: RegionId
  target: UpdatedTlsChain
  resetUploadTlsCertificateRequest: (regionId: RegionId, target: UpdatedTlsChain) => void
  certificate?: TlsPublicCertChain
  fetchTlsCertificateRequest: AsyncRequestState
  uploadTlsCertificate: (regionId: RegionId, target: UpdatedTlsChain, certificate: string) => void
  uploadTlsCertificateRequest: AsyncRequestState
}

interface State {
  showingCertificateChain: boolean
}

class Certificate extends Component<Props, State> {
  state: State = {
    showingCertificateChain: false,
  }

  componentWillUnmount() {
    const { regionId, target, resetUploadTlsCertificateRequest } = this.props
    resetUploadTlsCertificateRequest(regionId, target)
  }

  render() {
    return (
      <div>
        <EuiFormLabel>{this.renderCertificateTargetTitle()}</EuiFormLabel>

        <EuiSpacer size='s' />

        {this.renderContent()}
      </div>
    )
  }

  renderCertificateTargetTitle() {
    const { target } = this.props
    const { service } = target

    if (service === 'ui') {
      return (
        <FormattedMessage
          id='region-settings.tls-settings.cloud-ui'
          defaultMessage='Cloud UI and API'
        />
      )
    }

    if (service === 'proxy') {
      return <FormattedMessage id='region-settings.tls-settings.proxy' defaultMessage='Proxy' />
    }

    if (service === 'adminconsole') {
      return <FormattedMessage id='region-settings.tls-settings.ac' defaultMessage='Adminconsole' />
    }

    return null
  }

  renderContent() {
    const { certificate, fetchTlsCertificateRequest, uploadTlsCertificateRequest } = this.props

    if (fetchTlsCertificateRequest.error) {
      return <CuiAlert type='error'>{fetchTlsCertificateRequest.error}</CuiAlert>
    }

    if (!certificate) {
      return <EuiSkeletonText lines={1} />
    }

    const { showingCertificateChain } = this.state
    const certificateChain = certificate.chain.join('\n')
    const rootCert = certificate.chain[certificate.chain.length - 1]

    return (
      <Fragment>
        <div>
          <PermissionsGate
            permissions={[
              {
                type: 'tls',
                action: 'update',
              },
            ]}
          >
            {({ hasPermissions }) => (
              <CuiFileUploadButton
                disabled={uploadTlsCertificateRequest.inProgress || !hasPermissions}
                onChange={(file) => this.onFileReceived(file)}
              >
                <CuiButtonEmpty
                  disabled={!hasPermissions}
                  color='primary'
                  size='s'
                  iconType='importAction'
                  spin={uploadTlsCertificateRequest.inProgress}
                >
                  <FormattedMessage
                    id='region-settings.tls-settings.upload-new-certificate'
                    defaultMessage='Upload new certificate'
                  />
                </CuiButtonEmpty>
              </CuiFileUploadButton>
            )}
          </PermissionsGate>

          <EuiSpacer size='xs' />

          <CuiButtonEmpty
            color='primary'
            size='s'
            iconType='eye'
            onClick={() => this.setState({ showingCertificateChain: true })}
          >
            <FormattedMessage
              id='region-settings.tls-settings.show-certificate-chain'
              defaultMessage='Show certificate chain'
            />
          </CuiButtonEmpty>
        </div>

        {uploadTlsCertificateRequest.error && (
          <Fragment>
            <EuiSpacer size='m' />

            <CuiAlert type='error'>{uploadTlsCertificateRequest.error}</CuiAlert>
          </Fragment>
        )}

        {!uploadTlsCertificateRequest.error && uploadTlsCertificateRequest.isDone && (
          <Fragment>
            <EuiSpacer size='m' />

            <CuiAlert type='info'>
              <FormattedMessage
                id='region-settings.tls-settings.upload-successful'
                defaultMessage='Certificate uploaded successfully!'
              />
            </CuiAlert>
          </Fragment>
        )}

        {showingCertificateChain && (
          <EuiFlyout
            size='m'
            ownFocus={true}
            onClose={() => this.setState({ showingCertificateChain: false })}
          >
            <EuiFlyoutHeader>
              <EuiTitle>
                <h3>
                  <FormattedMessage
                    id='region-settings.tls-settings.certificate-chain-flyout-title'
                    defaultMessage='{certificateType} certificate chain'
                    values={{
                      certificateType: this.renderCertificateTargetTitle(),
                    }}
                  />
                </h3>
              </EuiTitle>
            </EuiFlyoutHeader>

            <EuiFlyoutBody>
              <CuiCodeBlock>{certificateChain}</CuiCodeBlock>
            </EuiFlyoutBody>

            <EuiFlyoutFooter>
              <EuiFlexGroup gutterSize='m' alignItems='center' justifyContent='spaceBetween'>
                <EuiFlexItem grow={false}>
                  <EuiFlexGroup>
                    <EuiFlexItem>
                      <CopyButton value={certificateChain}>
                        {(copyButtonProps) => (
                          <CuiButton fill={true} {...copyButtonProps}>
                            <FormattedMessage
                              id='region-settings.tls-settings.copy-chain-button-text'
                              defaultMessage='Copy chain'
                            />
                          </CuiButton>
                        )}
                      </CopyButton>
                    </EuiFlexItem>
                    {rootCert && (
                      <EuiFlexItem grow={false}>
                        <CopyButton value={rootCert}>
                          {(copyButtonProps) => (
                            <CuiButton fill={true} {...copyButtonProps}>
                              <FormattedMessage
                                id='region-settings.tls-settings.copy-root-button-text'
                                defaultMessage='Copy root certificate'
                              />
                            </CuiButton>
                          )}
                        </CopyButton>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </EuiFlexItem>

                <EuiFlexItem grow={false}>
                  <CuiButtonEmpty onClick={() => this.setState({ showingCertificateChain: false })}>
                    <FormattedMessage
                      id='region-settings.tls-settings.certificate-chain-close-flyout'
                      defaultMessage='Close'
                    />
                  </CuiButtonEmpty>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlyoutFooter>
          </EuiFlyout>
        )}
      </Fragment>
    )
  }

  onFileReceived(file) {
    const { regionId, target, uploadTlsCertificate } = this.props

    return readFile(file).then((certificate) => uploadTlsCertificate(regionId, target, certificate))
  }
}

export default Certificate
